/** Canonical wire contract, also imported by the PlayCanvas client. No server dependencies. */
export const PROTOCOL_VERSION = 1;
/**
 * 服务端状态批量转发的 tick 周期。批量只带 dirty 成员的最新 state，所以它是
 * 「运动玩家」的同步帧率上限（客户端上行 30Hz 分级见 walk-scene main.ts 的
 * STATE_SEND_* 常量）：50ms(20Hz) 会把 30Hz 上行降采样到 20Hz，取 33ms 对齐
 * 30Hz。静止玩家不受影响（不进批，靠 1s keyframe 兜底），出站消息量仅随
 * tick 线性小增，批内状态数反而大幅下降。
 */
export const SYNC_INTERVAL_MS = 33;
export const LEVEL_NAMES = ["新贵", "明俊", "豪杰", "泰斗", "白帝"] as const;
export const LEVEL_MAX = 50;
/** 每个称号覆盖 10 级：新贵 1–10、明俊 11–20、豪杰 21–30、泰斗 31–40、白帝 41–50。 */
export function levelTitle(level: number): string {
  const clamped = Math.min(Math.max(Math.trunc(level) || 1, 1), LEVEL_MAX);
  return LEVEL_NAMES[Math.min(LEVEL_NAMES.length - 1, Math.floor((clamped - 1) / 10))];
}
export type Vec3 = [number, number, number];
export type Quat = [number, number, number, number];
export type Locomotion = "idle" | "walk" | "run" | "jump" | "fly";
export interface Profile {
  id: number;
  username: string;
  organization: string;
  /** 隐身模式：其他客户端不渲染该玩家（模型 + 名牌）。 */
  hidden: boolean;
  level: number;
  avatarId: string;
  revision: number;
  /** 房间级禁言（服务端在快照/事件输出时按 room_mutes 叠加，非 Actor 固有属性）：
   *  被禁言者上行 mic 强制为 false，名牌显示禁言图标。可选，旧客户端忽略。 */
  muted?: boolean;
}
export interface Actor extends Profile {
  role: "user" | "host" | "admin";
}
export interface Room {
  id: string;
  name: string;
  sceneId: string;
  hostUserId: number;
  maxParticipants: number;
  passwordHash: string | null;
  status: "open" | "locked" | "closed";
  revision: number;
}
export interface Scene {
  id: string;
  name: string;
  manifest: Record<string, any>;
  revision: number;
  enabled: boolean;
  /** 场景级主持人口令（可空）：非空时本场景「主持人」按钮的授权口令用它，
   *  为空回落平台级 platform_settings/HOST_GRANT_CODE。 */
  hostGrantCode?: string | null;
}
export interface Avatar {
  id: string;
  name: string;
  modelUrl: string;
  config: Record<string, any>;
  revision: number;
  enabled: boolean;
}
export interface Portal {
  sourceRoomId: string;
  portalId: string;
  targetRoomId: string;
  targetSpawn: Vec3 | null;
  targetAngles: Vec3 | null;
  enabled: boolean;
  revision: number;
}
export interface InputState {
  membershipEpoch: number;
  poseRevision: number;
  seq: number;
  position: Vec3;
  rotation: Quat;
  velocity: Vec3;
  locomotion: Locomotion;
  grounded: boolean;
  /** 开麦状态（声网 RTC）：随 20Hz 状态上行同步，名牌旁显示开麦图标。可选，旧客户端不传。 */
  mic?: boolean;
}
export interface ActionState {
  actionId: string | null;
  actionSeq: number;
  startedAt: number;
  duration: number;
  loop: boolean;
}
export interface PlayerState extends InputState {
  userId: number;
  sampleTime: number;
  poseRevision: number;
  motionRevision: number;
  motionStartedAt: number;
}
export interface Player {
  profile: Profile;
  state: PlayerState;
  action: ActionState;
  status: "online" | "transferring" | "reconnecting";
  avatar: Avatar;
}
export interface Snapshot {
  serverEpoch: string;
  serverTime: number;
  serverTick: number;
  room: { id: string; name: string; sceneId: string; maxParticipants: number };
  memberRevision: number;
  players: Player[];
  /** 房间视频屏同步态（主持人控制，服务端权威）：晚进房/重连恢复用。
   *  screens 只含被主持人操作过的屏幕，未出现的按场景 manifest 默认行为。 */
  video: { revision: number; screens: Record<string, VideoSyncState> };
  /** 房间背景音乐同步态（主持人控制，服务端权威）：恒有值，未操作过的
   *  房间为停止态（trackId 空串）。晚进房/重连按锚点外推恢复进度。 */
  music: MusicSyncState;
}
export interface StateBatch {
  serverEpoch: string;
  serverTime: number;
  serverTick: number;
  roomId: string;
  states: PlayerState[];
}
export interface Admission {
  room: Room;
  scene: Scene;
  avatar: Avatar;
  membershipEpoch: number;
  spawn: Vec3;
  angles: Vec3;
  resumeToken: string;
}
export interface Transfer extends Admission {
  transferId: string;
}
export type Reply<T> =
  | { ok: true; data: T }
  | { ok: false; error: { code: string; message: string } };
/** 房间动态物体（主持人添加的 GLB 摆件）增量广播；全量恢复走
 *  GET /hub/rooms/:id/objects 拉取（规避 socket 单消息 16KB 上限）。 */
export interface RoomObjectsEvent {
  serverEpoch: string;
  roomId: string;
  /** 单调递增，客户端可丢弃过期事件。 */
  revision: number;
  action: "spawn" | "remove" | "move";
  /** spawn 时的完整 SceneObject（id 已由服务端生成）。 */
  object?: Record<string, any>;
  /** remove 时的目标物体 id；move 时亦带。 */
  id?: string;
  /** move 时的完整变换（Gizmo 拖拽结束上行）。 */
  transform?: {
    position: [number, number, number];
    rotation: [number, number, number];
    scale: [number, number, number];
  };
}
export interface RuntimeEvent {
  target: { roomId: string } | { userId: number };
  name: string;
  data: unknown;
  volatile?: boolean;
}
/** 场景视频屏同步状态（主持人操作，服务端权威，随房间内存驻留）。
 *  mediaTime + anchoredAt 为进度锚点：客户端按 serverTime - anchoredAt
 *  推算目标进度，抵消广播/入会延迟期间的播放推进。 */
export interface VideoSyncState {
  playing: boolean;
  mediaTime: number;
  anchoredAt: number;
  visible: boolean;
  muted: boolean;
  /** 单屏单调递增，客户端丢弃过期/重复事件。 */
  seq: number;
}
/** 主持人视频控制增量广播（room:video）；全量随 room:snapshot 下发。 */
export interface RoomVideoEvent {
  serverEpoch: string;
  roomId: string;
  revision: number;
  screenId: string;
  state: VideoSyncState;
}
/** 房间背景音乐同步状态（主持人操作，服务端权威，随房间内存驻留）。
 *  进度锚点与 VideoSyncState 同构（mediaTime+anchoredAt 按 serverTime
 *  外推）；volume 为全场统一音量 0–1；trackId 空串 = 停止。trackUrl/
 *  trackName 随状态下发，客户端免查曲目库即可起播/显示。 */
export interface MusicSyncState {
  playing: boolean;
  trackId: string;
  trackUrl: string;
  trackName: string;
  volume: number;
  mediaTime: number;
  anchoredAt: number;
  /** 单调递增，客户端丢弃过期/重复事件。 */
  seq: number;
}
/** 主持人背景音乐控制增量广播（room:music）；全量随 room:snapshot 下发。 */
export interface RoomMusicEvent {
  serverEpoch: string;
  roomId: string;
  revision: number;
  state: MusicSyncState;
}
